import { html, LitElement, PropertyValues } from "lit";
import { property } from "lit/decorators/property.js";
import { state } from "lit/decorators/state.js";
import { MissingConfig } from "../../utils/beam-errors";
import { deleteCookieValue, getCookieValue } from "../../utils/cookies";
import { cssReset } from "../../shared/css-reset";
import { defineCustomElement } from "../../shared/defineCustomElement";
import { createScopedLocalStorage } from "../../utils/local-storage";
import { AsyncController } from "../../shared/controllers/async-controller";
import { _errorMessage } from "../../shared/fragments/error-message-template";
import { enforceConfig } from "../../shared/enforce-config";
import { LANGUAGES } from "../../api-sdk/types";
import { DEFAULT_BASE_URL } from "../../api-sdk/constants";
import { postTransaction } from "../../api-sdk/v4/routes";
import "../redeem-transaction";
import "../impact-overview";
import { BEAM_CART_COOKIE_NAME, TBeamCartLocalStorage, TCart } from "../../shared/cart-contents";
import { BeamNonprofitSelectEvent, BeamOrderCreatedEvent } from "../../utils/events";
import {
  isEmptyPromoData,
  parseJsonStringArray,
  formatStoreAndReturnPromoCodes,
  getAllPromoCodesFromCookie,
  mapToUnvalidatedPromoCodes,
} from "../../utils/promoManager";
import { PROMO_CODES_COOKIE_NAME, PROMO_LOCAL_STORAGE_KEY, TPromoCodeRequest } from "../../shared/promo-types";

const REQUIRED_DATA_PROPS = ["baseUrl", "apiKey", "orderId", "cartTotal", "currencyCode", "lang"];
const OPTIONAL_DATA_PROPS = [
  "storeId",
  "postalCode",
  "countryCode",
  "cart",
  "discountCodes",
  "promoCodes",
  "htmlPromoCodes",
  "email",
  "partnerUserId",
];

export class BeamPostPurchase extends LitElement {
  static tagName = "beam-post-purchase";

  @property({ type: String })
  public baseUrl: string = DEFAULT_BASE_URL;

  @property({ type: String })
  public apiKey!: string;

  @property({ type: Number, reflect: true })
  public storeId?: number; // Either storeId or postalCode, countryCode must be provided

  @property({ type: String })
  public postalCode?: string;

  @property({ type: String })
  public countryCode?: string;

  @property({ type: String })
  public orderId!: string;

  /**
   * Either email or partnerUserId is required
   */
  @property({ type: String })
  public email?: string;

  /**
   * Either email or partnerUserId is required
   */
  @property({ type: String })
  public partnerUserId?: string;

  @property({ type: Number })
  public cartTotal!: number;

  @property({ type: String })
  public currencyCode = "USD";

  @property({ type: String })
  public lang: LANGUAGES = "en";

  @property({ type: Object })
  public cart?: TCart;

  /**
   * @deprecated Use `promoCodes` instead. `discountCodes` is retained for backward compatibility
   */
  @property({ type: Array })
  public discountCodes?: string[];

  @property({ type: Array })
  public promoCodes?: string[];

  @property({ type: String })
  public htmlPromoCodes?: string;

  @property({ type: String })
  public domain?: string;

  @property({ type: Boolean })
  public debug = false;

  @state() private activeWidget: "redeem-transaction" | "impact-overview" | null = null; // wait for postTransaction
  @state() private nonprofitId: number | null = null; // from localStorage or redeem-transaction result
  @state() private selectionId: string | null = null; // from localStorage
  @state() private cartId: string | null = null; // from localStorage, external cart ID
  @state() private beamCartId: string | null = null; // from cookie, Beam cart ID
  @state() private userId: string | null = null; // from postTransaction
  @state() private transactionId: string | null = null; // from postTransaction
  @state() private excludeDonationsForRecurringTransactions = false;
  @state() private creationMethod = "";
  @property({ type: Boolean }) public draftConfig = false;

  private localStorage = createScopedLocalStorage(this);

  get parsedPromoCodes(): string[] {
    return parseJsonStringArray(this.htmlPromoCodes);
  }

  firstUpdated(_changedProperties: PropertyValues) {
    this.enforceConfig();
    this.selectionId = this.localStorage.getItem("transaction") || null;
    this.cartId = this.localStorage.getItemJson<TBeamCartLocalStorage>("cart")?.cartId || null;
    // Reading beamCartId from cookies instead of localStorage allows it to work across subdomains
    this.beamCartId = getCookieValue(BEAM_CART_COOKIE_NAME) || null;

    if (this.selectionId && this.nonprofitId) {
      this.activeWidget = "impact-overview";
    }
  }

  async updated(previousPropertyValues: PropertyValues) {
    // Also fires on first load as props go from undefined => value
    const refreshOnChangeProps = [...REQUIRED_DATA_PROPS, ...OPTIONAL_DATA_PROPS];
    if (refreshOnChangeProps.some((p) => previousPropertyValues.has(p))) {
      await this.transactionDataController.exec();
    }
  }

  enforceConfig() {
    enforceConfig(REQUIRED_DATA_PROPS, this);
    if (!this.email && !this.partnerUserId) {
      throw new MissingConfig(
        [this.email ? "" : "email", this.partnerUserId ? "" : "partnerUserId"].filter(Boolean),
        "At least one of [email, partnerUserId] must be provided"
      );
    }
  }

  private getManualPromoCodes(): string[] {
    if (this.parsedPromoCodes?.length) {
      return this.parsedPromoCodes;
    }
    if (this.promoCodes?.length) {
      return this.promoCodes;
    }
    return [];
  }

  handlePostTransaction = async () => {
    this.enforceConfig();
    const manualPromoCodes = this.getManualPromoCodes();
    const formattedPromosFromLocalStorage = formatStoreAndReturnPromoCodes(
      manualPromoCodes.map((code) => ({ value: code })),
      this.apiKey
    );

    const promoCookieData = getAllPromoCodesFromCookie();
    const formattedPromosFromCookie: TPromoCodeRequest = {
      unvalidatedPromoCodes: mapToUnvalidatedPromoCodes(manualPromoCodes.map((code) => ({ value: code }))),
      validatedPromoCodes: promoCookieData?.validatedPromoCodes ?? [],
    };

    const formattedPromos = formattedPromosFromLocalStorage ?? formattedPromosFromCookie;

    const shouldSendPromos = formattedPromos && !isEmptyPromoData(formattedPromos);
    const result = await postTransaction({
      baseUrl: this.baseUrl,
      apiRoot: "/api",
      headers: {
        authorization: `Api-Key ${this.apiKey}`,
      },
      requestBody: {
        selectionId: this.selectionId || undefined,
        cartId: this.cartId || undefined,
        beamCartId: this.beamCartId || undefined,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        storeId: this.storeId,
        orderId: this.orderId,
        cartTotal: this.cartTotal,
        email: this.email, // One or both of email & partnerUserId should be defined
        partnerUserId: this.partnerUserId, // One or both of email & partnerUserId should be defined
        currencyCode: this.currencyCode,
        postalCode: this.postalCode,
        countryCode: this.countryCode,
        cart: this.cart,
        ...(shouldSendPromos && { promos: formattedPromos }),
        discountCodes: this.discountCodes,
      },
    });

    this.transactionId = result.transactionId;
    this.userId = result.beamUserId;
    this.nonprofitId = result.nonprofitId;
    this.excludeDonationsForRecurringTransactions = Boolean(result.reportSettings?.excludeRecurringDonations);
    this.creationMethod = result.creationMethod || "";

    const isTransactionRedeemed = !!this.nonprofitId;
    this.activeWidget = isTransactionRedeemed ? "impact-overview" : "redeem-transaction";

    // Emit order created event only when order is registered with Beam for the first time
    if (result.transactionCreated) {
      const localStorageKeys = ["transaction", "cart", PROMO_LOCAL_STORAGE_KEY];
      localStorageKeys.forEach((key) => {
        this.localStorage.removeItem(key);
      });
      const cookieConfigs = [
        { name: BEAM_CART_COOKIE_NAME, path: "/", domain: this.domain },
        { name: PROMO_CODES_COOKIE_NAME, path: "/", domain: this.domain },
      ];

      cookieConfigs.forEach((config) => deleteCookieValue(config));

      // Emit order created event only when order is registered with Beam for the first time
      dispatchEvent(
        new BeamOrderCreatedEvent({
          transactionId: result.transactionId,
          orderId: this.orderId,
          cartTotal: this.cartTotal,
          currencyCode: this.currencyCode,
        })
      );
    }
    return result;
  };

  private transactionDataController = new AsyncController<typeof this.handlePostTransaction>(
    this,
    this.handlePostTransaction
  );

  render() {
    if (this.transactionDataController.loading) {
      return "";
    }
    if (this.transactionDataController.error) {
      if (this.debug) {
        return _errorMessage({ error: this.transactionDataController.error });
      }
      return "";
    }
    if (this.excludeDonationsForRecurringTransactions && this.creationMethod === "recurring") {
      return html``;
    }
    if (this.activeWidget === "redeem-transaction") {
      return html`
        <beam-redeem-transaction
          part="redeem-transaction"
          baseUrl="${this.baseUrl}"
          apiKey="${this.apiKey}"
          storeId="${this.storeId}"
          postalCode="${this.postalCode}"
          countryCode="${this.countryCode}"
          transactionId="${this.transactionId}"
          cart="${JSON.stringify(this.cart)}"
          lang="${this.lang}"
          excludeDonationsForRecurringTransactions="${this.excludeDonationsForRecurringTransactions}"
          ?draftConfig="${this.draftConfig}"
          ?debug="${this.debug}"
          @beamnonprofitselect="${(event: Event) => {
            if (!(event instanceof CustomEvent)) return; // TS guard
            const { selectedNonprofitId } = (event as BeamNonprofitSelectEvent).detail;
            this.nonprofitId = selectedNonprofitId ?? null;
            this.activeWidget = "impact-overview";
          }}"
        ></beam-redeem-transaction>
      `;
    }
    if (this.activeWidget === "impact-overview") {
      return html`
        <beam-impact-overview
          part="impact-overview"
          storeId="${this.storeId}"
          baseUrl="${this.baseUrl}"
          apiKey="${this.apiKey}"
          userId="${this.userId}"
          nonprofitId="${this.nonprofitId}"
          lang="${this.lang}"
          discountCodes="${JSON.stringify(this.discountCodes)}"
          transactionId="${this.transactionId}"
          ?draftConfig="${this.draftConfig}"
          ?debug="${this.debug}"
        ></beam-impact-overview>
      `;
    }
    return "";
  }

  static styles = [cssReset];
}

defineCustomElement(BeamPostPurchase);

declare global {
  interface HTMLElementTagNameMap {
    "beam-post-purchase": BeamPostPurchase;
  }
}
